<script lang="ts" setup>
    import { computed, ref, onMounted, watch } from 'vue';
    import TextInput from '../components/TextInput.vue';
    import ActionButtonV2 from "../components/ActionButtonV2.vue"; 
    import DropDownSplitButton from '../components/DropDownSplitButton.vue'
    import DropDownButtonItem from '../components/DropDownButtonItem.vue'
    import { DefaultActionButtonValues,ActionButtonStyle } from '../lib/Types/Types';
    import { IToolbar, IToolbarItem, ToolbarSize, ToolbarStyle,ToolbarBackground, IToolbarItemTypes, IToolbarActionButtonItem, IToolbarSearchActionItem, 
             ToolbarPadding, IToolbarSearchItem, IToolbarDropDownMenuItem, 
             ToolbarItemType,
             IToolbarDivItem} from '../lib/Toolbar';
    import LoadingSection from './LoadingSection.vue';
    import { IconTypes } from '../Enums/UiEnums';
    import ToolbarDivItem from './toolbar/ToolbarDivItem.vue';

    const emit = defineEmits(["toolbarClick","toolbarSearchText"]);
    
    const props =  withDefaults(defineProps<
    {
        configuration: IToolbar,
        toolbarSize?: ToolbarSize,
        toolbarStyle: ToolbarStyle
        toolbarBackground? : ToolbarBackground
        spin?: boolean,
        toolbarPadding?: ToolbarPadding
        menuStyle?: string,
        styleAsOfficeMd: boolean,
        toolbarClass?: string
    }>(), {
        toolbarSize: ToolbarSize.regular,
        toolbarStyle: ToolbarStyle.outline,
        toolbarBackground: ToolbarBackground.transparent,
        toolbarPadding: ToolbarPadding.paddingAll,
        menuStyle: "",
        styleAsOfficeMd: false,
        toolbarClass: ""
    });

    const intervalId = ref<number>(0);
    const toolbarData = ref<IToolbarItemTypes[]>([]);
    const showSearchSpinner = ref<boolean>(false);

    onMounted(async () => {
        toolbarData.value = [...props.configuration.toolbarList];
    });

    watch(() => props.configuration, (newConfiguration,oldConfiguration) => { 
        toolbarData.value = [...newConfiguration.toolbarList]
    }, {deep: true});

    const IsOfficeMdStyle = ref<boolean>(props.styleAsOfficeMd ?? false);
    const OfficeMdStyleClass = ref<string>("isalus-menu-item");
    const OfficeMdSlotStyleClass = ref<string>("isalus-menu-item-slot align-self-center");
    const OfficeMdFirstSlotStyleClass = ref<string>("isalus-menu-item-slot-first");
    const OfficeMdButtonGroupClass = ref<string>("isalus-menu-buttongroup");
    const OfficeMdHighlightClass = ref<string>('isalus-menu-highlight');

    const actionButtonClass = computed<string>(() => {
        const size = props.toolbarSize === ToolbarSize.regular
            ? "label-isalus-small"
            : props.toolbarSize === ToolbarSize.small
                ? "label-isalus-small pt-0 pb-0"
                : "";

        const background = props.toolbarBackground;

        if (IsOfficeMdStyle.value) {
            return `isalus-menu-item ${size} ${background}`;
        } else {
            const style = props.toolbarStyle === ToolbarStyle.outline
            ? "btn-outline-primary"
            : "btn-primary";
            return `btn ${style} ${size} ${background}`;
        }
    });

    const dropdownClass = computed<string>(() => `dropdown-item ${props.toolbarSize === ToolbarSize.regular || props.toolbarSize === ToolbarSize.small ? "label-isalus-small" : ""}`);
    const dropdownDisabledClass = computed<string>(() => `dropdown-item-text text-black-50 ${props.toolbarSize === ToolbarSize.regular || props.toolbarSize === ToolbarSize.small ? "label-isalus-small" : ""}`);

    const toolbarPaddingClass = computed<string>(()=> {
        return props.toolbarPadding == ToolbarPadding.paddingOnlyLeftRight ? "row ps-2 pe-2" : "row p-2";
    });  

    // base class for toolbar
    const ToolbarBaseClass = computed<string>(() => (IsOfficeMdStyle.value ? OfficeMdStyleClass.value : ""));
    const ToolbarSlotClass = computed<string>(() => (IsOfficeMdStyle.value ? OfficeMdSlotStyleClass.value : ""));
    const ToolbarTemplateClass = computed<string>(() => (IsOfficeMdStyle.value ? OfficeMdSlotStyleClass.value : 'col-auto gx-1'));

    function MenuButtonSlotIcon(item: IToolbarItem) {
        let slotIcon = "";
        if (item.action !== undefined) {
            let result = DefaultActionButtonValues(item.action);
            if (result.iconClass !== undefined)
                slotIcon = result.iconClass;
        }
        return slotIcon;
    }

    async function ToolbarButtonClick(contextValue: string|number|undefined, item: IToolbarItem) {
        if ("performAction" in item)
        {
            await (<IToolbarActionButtonItem>item).performAction(contextValue);
        }
        else
        {
            // Making backwards compatiable.  Someday remove?
            if (contextValue)
                emit("toolbarClick", contextValue);
            else
                emit("toolbarClick", item.identifier);
        }
    }

    async function ToolbarDivClick(contextValue: string|number|undefined, item: IToolbarItem) {

        if ("performAction" in item && item.performAction)
        {
            await (<IToolbarDivItem>item).performAction!(contextValue);
        }
        else
        {
            // Making backwards compatiable.  Someday remove?
            if (contextValue)
                emit("toolbarClick", contextValue);
            else
                emit("toolbarClick", item.identifier);
        }
    }
    
    async function ToolbarMenuClick(contextValue: string | number | undefined, menu: IToolbarDropDownMenuItem) {
        if ("performAction" in menu)
        {
            await (<IToolbarActionButtonItem>menu).performAction(contextValue);
        }
        else
        {
            // Making backwards compatiable.  Someday remove?
            if (contextValue)
                emit("toolbarClick", contextValue);
            else
                emit("toolbarClick", menu.identifier);
        }
    }

    function SearchItem(index: number, item: IToolbarItem) {
        if (intervalId.value != 0)
            window.clearInterval(intervalId.value);

        // Setup the search to occur 1 second after we stop typing
        intervalId.value = window.setInterval(async () => { 
            window.clearInterval(intervalId.value);

            await callSearchHandlerImmediately(index, item);
            
        }, toolbarData.value[index].search?.keyPressDelay ? 1000 : 10);
    }

    async function callSearchHandlerImmediately(index: number, item: IToolbarItem, forceNewSearch?: boolean)
    {
        if ("searchAction" in item)
        {
            showSearchSpinner.value = true;
            await (<IToolbarSearchActionItem>item).searchAction(toolbarData.value[index].identifier, toolbarData.value[index].search?.searchText, forceNewSearch);
            showSearchSpinner.value = false;
        }
        else
        {
            emit("toolbarSearchText", toolbarData.value[index].identifier, toolbarData.value[index].search?.searchText);
        }
    }

    function UpdateSearch(searchTerm: string | undefined, index: number, item: IToolbarItem) {
        const searchInput = toolbarData.value[index].search;
        if(searchInput) {
            searchInput.searchText = searchTerm; 
            SearchItem(index, item);
        }
    }

    function isToolbarItemVisible(toolbarItem: IToolbarItemTypes)
    {
        if (toolbarItem.hidden ?? false)
            return false;
        if (toolbarItem.search)
            return true;
        if ("menuList" in toolbarItem && toolbarItem.menuList.length)
            return true;
        if (!("menuList" in toolbarItem))
            return true;            
        return false;
    }

    async function callSearchFocus(toolbarItem?: IToolbarSearchItem)
    {
        if (!toolbarItem?.onfocus)
            return;
        toolbarItem.onfocus();
    }

    async function callSearchBlur(toolbarItem?: IToolbarSearchItem)
    {
        if (!toolbarItem?.onblur)
            return;
        toolbarItem.onblur();
    }

    function clearSearchText(item: IToolbarItemTypes, index: number)
    {
        if (!item.search)
            return;

        item.search.searchText = ""; 
        callSearchHandlerImmediately(index, item, true);
    }
    
</script>
<style scoped>
.span-column {
  display: flex;
  flex-direction: column;
}

.long-line {
  border-top: 1px solid rgb(124, 122, 122);
}

.searchInput {
    width:10rem
}
.searchInProgressInput {
    width:10rem; 
    background-color: lightgray;
}
</style>
<template>

    <div :class="IsOfficeMdStyle ? 'col d-flex' : toolbarPaddingClass"> 
        <template v-for="(item, index) in toolbarData.filter(tbItem => isToolbarItemVisible(tbItem))">
            <div 
                :class="[ToolbarTemplateClass,
                    (IsOfficeMdStyle && item.identifier === 0) ? OfficeMdFirstSlotStyleClass : '',
                    item.toolbarClass]"
                :tst="item.identifier">
                <!-- Search Input -->
                <template :class="{OfficeMdStyleClass : IsOfficeMdStyle}" v-if="item.search">
                    <span class="searchInput">
                        <TextInput 
                            v-if="!showSearchSpinner && item.toolBarItemType !== ToolbarItemType.div"
                            v-model:text-value="item.search.searchText" 
                            :maxLength="item.search.maxLength"
                            :placeholder="item.search.placeholder"
                            :disabled="!(item.enabled ?? true)"
                            :title="item.tooltip"
                            cssFramework="search" 
                            @focus="() => callSearchFocus(item.search)"
                            @blur="() => callSearchBlur(item.search)"
                            @update:textValue="newVal => UpdateSearch(newVal?.toString(), index, item)"
                            :suffixIcon="IconTypes.Remove"
                            suffixTitle="Clear search text and execute search"
                            @suffixIconClicked="() => clearSearchText(item, index)">
                        </TextInput>
                    </span>
                    <span v-if="showSearchSpinner" class="small_text_start small_text_other_search searchInProgressInput">
                        <LoadingSection :isLoading="true" :loadingMsg="item.search.searchText" />
                    </span>
                </template>
    
                <ActionButtonV2 v-if="!item.search && !('menuList' in item) && item.toolBarItemType !== ToolbarItemType.div"
                    :action="item.action ?? ActionButtonStyle.Blank" 
                    :class="item.buttonClass ?? actionButtonClass"
                    :contextValue="item.identifier" 
                    :text="item.label"
                    :width="item.width" 
                    :tooltip="item.tooltip"
                    :disabled="!(item.enabled ?? true)"
                    :performAction="async(contextValue) => ToolbarButtonClick(contextValue, item)">
                </ActionButtonV2>

                <ToolbarDivItem v-if="item.toolBarItemType === ToolbarItemType.div"
                    :content = "item.divContent"
                    :contextValue = "item.identifier"
                    :baseClass = "ToolbarBaseClass"
                    :tooltip = "item.tooltip"
                    :onMouseEnterClass="IsOfficeMdStyle ? OfficeMdHighlightClass : item.onMouseEnterClass"
                    :enabled = "item.enabled"
                    :performAction="async(contextValue) => ToolbarDivClick(contextValue, item)">
                </ToolbarDivItem>
    
                <DropDownSplitButton v-if="'menuList' in item && item.menuList && item.search === undefined && item.toolBarItemType !== ToolbarItemType.div" 
                    :baseClass = "IsOfficeMdStyle ? OfficeMdStyleClass : actionButtonClass"
                    :onMouseEnterClass="IsOfficeMdStyle ? OfficeMdHighlightClass : item.onMouseEnterClass"
                    :buttonTitle="item.tooltip"
                    :disabled="!(item.enabled ?? true)"
                    :buttonGroupClass="IsOfficeMdStyle ? OfficeMdButtonGroupClass : ''"
                    :dropdownStyle="menuStyle"
                    :addMarginEnd="false"
                    @onButtonClick="async(contextValue) => ToolbarButtonClick(contextValue, item)">
                    <template v-slot:button>
                        <i :class=MenuButtonSlotIcon(item)></i> {{item.label}}<em v-if="props.spin" class="fa-solid fa-spinner fa-spin ms-1 me-1"></em>
                    </template>
                    <div v-for="menu in item.menuList">
                        <div v-show="menu.label === '-'"  class="span-column">
                            <div class="long-line"></div>
                        </div>
                        <DropDownButtonItem v-show="menu.label !== '-'" v-bind:data="menu.identifier" 
                            :baseClass="dropdownClass" 
                            :disabledClass="dropdownDisabledClass"
                            :enabled="menu.enabled ?? true"
                            v-on:click="async(contextValue) => ToolbarMenuClick(contextValue, menu)">
                            {{menu.label}}
                        </DropDownButtonItem>
                    </div>
                </DropDownSplitButton>    
            </div>
        </template>
    </div>
</template>